import { AVAILABLE_VIEWS_PATH } from '../contracts/httpPaths';
import {
  AvailableViewsResponseBody,
  toAvailableViewSummary,
} from '../contracts/availableViews';
import type { Recipe } from '../contracts/recipes';
import { getRefreshDataHeader } from 'utils/getRefreshDataHeader';

import type { DefectOverlayTenantOptions } from '../defectOverlays/tenantDefectOverlayAdapter';
import { mergeDefectOverlayViews } from '../defectOverlays/mergeDefectOverlayViews';
import { surveyDataManagerClient, toSurveyDataManagerApiError } from './httpClient';
import { buildMockAvailableViewsResponse } from './availableViews.mock';
import { isScannerMockCatalogueEnabled } from './scannerMockResponses';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function looksLikeRecipe(value: unknown): value is Recipe {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    Array.isArray(value.components)
  );
}

/**
 * Normalise API payloads that may return `{ views }` or `{ recipes }`.
 */
export function normalizeAvailableViewsResponse(
  payload: unknown,
): AvailableViewsResponseBody | null {
  if (!isRecord(payload)) {
    return null;
  }

  const rawViews = payload.views ?? payload.recipes;
  if (!Array.isArray(rawViews) || rawViews.length === 0) {
    return null;
  }

  const recipes = rawViews.filter(looksLikeRecipe);
  if (recipes.length === 0) {
    return null;
  }

  return {
    views: recipes.map((recipe) =>
      'componentCount' in recipe && typeof recipe.componentCount === 'number'
        ? (recipe as AvailableViewsResponseBody['views'][number])
        : toAvailableViewSummary(recipe),
    ),
  };
}

function shouldPreferMockCatalogue(): boolean {
  return isScannerMockCatalogueEnabled();
}

export async function getAvailableViews(
  refreshData: boolean = false,
  options?: DefectOverlayTenantOptions,
): Promise<AvailableViewsResponseBody> {
  if (shouldPreferMockCatalogue()) {
    return mergeDefectOverlayViews(buildMockAvailableViewsResponse(), options);
  }

  try {
    const response = await surveyDataManagerClient.get<unknown>(
      AVAILABLE_VIEWS_PATH,
      getRefreshDataHeader(refreshData),
    );
    const normalized = normalizeAvailableViewsResponse(response.data);
    if (normalized && normalized.views.length > 0) {
      return mergeDefectOverlayViews(normalized, options);
    }
    return mergeDefectOverlayViews(buildMockAvailableViewsResponse(), options);
  } catch (error) {
    const apiError = toSurveyDataManagerApiError(error);
    if (apiError.status === 404 || apiError.status === 0 || apiError.status >= 500) {
      return mergeDefectOverlayViews(buildMockAvailableViewsResponse(), options);
    }
    throw apiError;
  }
}
